// Firebase-klienten och de två sakerna portalen behöver den till:
// inloggning och uppladdning till Storage.
//
// Backenden får aldrig se filen. Klienten laddar upp till Storage och skickar
// bara sökvägen vidare (AA-5 steg 1–2), vilket betyder att `storage.rules` är
// den enda spärren i det ögonblicket — se den filen för varför behörigheten
// är custom claimen och inte e-postdomänen.
import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { getStorage, ref, uploadBytes } from "firebase/storage";

const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
});

export const auth = getAuth(app);
const storage = getStorage(app);

export function watchUser(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function signIn() {
  const provider = new GoogleAuthProvider();
  // `hd` föreslår rätt konto i Googles ruta och hindrar utomstående. Det är
  // BEKVÄMLIGHET OCH ETT FILTER, inte behörigheten: eleverna har konton i
  // samma Workspace-domän, och det som avgör är custom claimen schoolAdmin
  // som backenden läser ur tokenen (AA-4).
  const hd = import.meta.env.VITE_GOOGLE_HD;
  if (hd) provider.setCustomParameters({ hd });
  await signInWithPopup(auth, provider);
}

export function signOutUser() {
  return signOut(auth);
}

/**
 * Skolan den inloggade administrerar, eller null.
 *
 * Läses ur ID-tokenens claims bara för att kunna VISA rätt sak och säga ifrån
 * tidigt. Varje anrop kontrolleras ändå server-side; en klient som ljuger om
 * sin claim kommer ingenstans.
 */
export async function schoolOf(user, forceRefresh = false) {
  if (!user) return null;
  // `forceRefresh` hämtar en NY token från Firebase i stället för den cachade.
  // Det behövs i exakt ett läge — direkt efter att /admin/session satt claimen
  // — och bara där: claims ligger i tokenen, och den vi redan har utfärdades
  // innan claimen fanns. Utan force ser användaren sig som obehörig i upp till
  // en timme trots att behörigheten är satt.
  const token = await user.getIdTokenResult(forceRefresh);
  return token.claims.schoolAdmin || null;
}

export async function idToken(user) {
  return user.getIdToken();
}

/** Laddar upp filen och returnerar dess storage-sökväg. */
export async function uploadFile(user, schoolId, file) {
  // Ett id per uppladdning, så att två filer med samma namn inte skriver över
  // varandra. Formen är AA-5:s: schools/{school_id}/uploads/{doc_id}/{filnamn}
  const docId = crypto.randomUUID();
  const path = `schools/${schoolId}/uploads/${docId}/${file.name}`;
  await uploadBytes(ref(storage, path), file);
  return path;
}
